/** Public RPC shapes; the desktop never imports server or database packages. */
export interface Source {
  environmentId: string;
  kind: string;
  externalId: string;
  sourceVersion: string;
}
export interface RecordCopy extends Source {
  environmentLabel: string;
  observedAt: string;
  record: {
    kind: string;
    externalId: string;
    title: string;
    status: string;
    fields: { key: string; label: string; value: string }[];
  };
}
export interface Draft {
  response: string;
  sources: Source[];
}
export interface SavedDraft {
  response: string;
  sources: RecordCopy[];
}
export interface Attempt {
  assignmentId: string;
  revision: number;
  startedAt: string;
  deadlineAt: string;
  savedAt: string | null;
  submittedAt: string | null;
  draft: SavedDraft;
  serverNow: string;
  expired: boolean;
}
export interface Criterion {
  id: string;
  label: string;
  maxPoints: number | null;
}
export interface Definition {
  situation: string;
  instructions: string;
  annexes: { title: string; text: string }[];
  rubric: Criterion[];
}
export interface SubjectForm {
  title: string;
  format: 'written' | 'cms';
  minutes: number;
  definition: Definition;
  solution: string;
  hints: string;
  allowHints: boolean;
  userIds: string[];
  annexFileIds?: string[];
}
export interface PracticeTemplate {
  id: string;
  version: string;
  company: string;
  competency: string;
  preparation: string;
  subject: Omit<SubjectForm, 'minutes' | 'hints' | 'userIds'> & {
    durationSeconds: number;
    hints: string[];
  };
}
export function applyPracticeTemplate(
  current: SubjectForm,
  template: PracticeTemplate,
): SubjectForm {
  const subject = structuredClone(template.subject);
  return {
    title: subject.title,
    format: subject.format,
    minutes: subject.durationSeconds / 60,
    definition: subject.definition,
    solution: subject.solution,
    hints: subject.hints.join('\n\n'),
    allowHints: subject.allowHints,
    userIds: [...current.userIds],
    annexFileIds: [...(current.annexFileIds ?? [])],
  };
}
export function hasSubjectContent(subject: SubjectForm): boolean {
  return Boolean(
    subject.title.trim() ||
    subject.annexFileIds?.length ||
    subject.solution.trim() ||
    subject.hints.trim() ||
    subject.definition.situation.trim() ||
    subject.definition.instructions.trim() ||
    subject.definition.annexes.length ||
    subject.definition.rubric.some((c) => c.label.trim()),
  );
}
export interface Assignment {
  id: string;
  subjectId: string;
  userId: string;
  userName: string;
  title: string;
  format: 'written' | 'cms';
  durationSeconds: number;
  startedAt: string | null;
  submittedAt: string | null;
  expired: boolean | null;
}
export interface Submission {
  id: string;
  revision: number;
  final: boolean;
  createdAt: string;
}
export interface Review {
  id: string;
  note: string;
  scores: { criterionId: string; points: number | null }[];
  published: boolean;
  authorName: string;
  createdAt: string;
}
export interface PracticeRead {
  files: PracticeFile[];
  assignment: Assignment;
  attempt: Attempt | null;
  definition: Definition | null;
  hints: string[];
  solution: string | null;
  privateSolution?: string | null;
  submissions: Submission[];
  totalSubmissions: number;
  selectedSubmission: (Submission & { body: SavedDraft; reviews: Review[] }) | null;
}
export interface PracticeFile {
  id: string;
  name: string;
  mime: string;
  sizeBytes: number;
  url: string | null;
  used: boolean;
}
export interface LocalDraft {
  revision: number;
  value: Draft;
}
export function sourceKey(source: Source): string {
  return JSON.stringify([source.environmentId, source.kind, source.externalId]);
}
export function editable(draft: SavedDraft): Draft {
  return {
    response: draft.response,
    sources: draft.sources.map(({ environmentId, kind, externalId, sourceVersion }) => ({
      environmentId,
      kind,
      externalId,
      sourceVersion,
    })),
  };
}
export function sameDraft(a: Draft, b: Draft): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
/** Based on the server clock and elapsed monotonic time, never the editable device wall clock. */
export function remainingSeconds(deadline: string, serverNow: string, elapsedMs: number): number {
  return Math.max(
    0,
    Math.ceil((Date.parse(deadline) - Date.parse(serverNow) - Math.max(0, elapsedMs)) / 1000),
  );
}
export function timeLabel(seconds: number): string {
  return `${Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}`;
}
export function dateLabel(value: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'Europe/Paris',
  }).format(Date.parse(value));
}
